import {
  MAX_VALHALLA_LOCATIONS,
  VALHALLA_ROUTE_ENDPOINT,
} from "../../clients/valhallaClient";
import {
  QuickTripCheckReturn,
  type Valhalla_CostingModel,
  type Valhalla_Location,
  type Valhalla_RouteError,
  type Valhalla_RouteRes,
  type ValhallaRouteResult,
} from "../../types/valhallaTypes";

/**
 * Fetches a route plan from the Valhalla routing service.
 *
 * Sends a request to the Valhalla routing API with the provided locations and costing model.
 * Returns a Valhalla route response if successful or an error response if there is a problem
 * with the request. If an error occurs during the fetch operation, it logs the error and
 * returns undefined.
 *
 * @param locations - An array of Valhalla_Location objects representing the waypoints of the route.
 * @param costing - The costing model to use for the route calculation. Defaults to "auto".
 * @returns A promise that resolves to either a Valhalla_RouteRes object if the route is successfully
 * fetched, a Valhalla_RouteError object if there is an error in the response, or undefined if an
 * exception occurs during the fetch operation.
 */
export async function fetchValhallaRoutePlan(
  locations: Valhalla_Location[],
  costing: Valhalla_CostingModel = "auto",
): Promise<ValhallaRouteResult> {
  try {
    const json = {
      locations: locations,
      costing: costing,
    };

    const res = await fetch(
      `${VALHALLA_ROUTE_ENDPOINT}?json=${JSON.stringify(json)}`,
    );

    const body = await res.json();

    if (res.ok) {
      return {
        success: true,
        route: body as Valhalla_RouteRes,
      };
    }

    return {
      success: false,
      error: body as Valhalla_RouteError,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        error_code: -1,
        error: error instanceof Error ? error.message : "Unknown error",
        status_code: 500,
        status: "Client Error",
      },
    };
  }
}

export async function quickTripCheck(
  locations: Valhalla_Location[],
  costing: Valhalla_CostingModel = "auto",
): Promise<QuickTripCheckReturn> {
  const response = await fetchValhallaLongTrip({ locations, costing });

  if (response.success) {
    return QuickTripCheckReturn.routable;
  } else if (response.error.error_code === 170) {
    return QuickTripCheckReturn.unconnected_regions;
  } else {
    return QuickTripCheckReturn.not_routable;
  }
}

export async function fetchValhallaLongTrip({
  locations,
  costing = "auto",
}: {
  locations: Valhalla_Location[];
  costing?: Valhalla_CostingModel;
}): Promise<ValhallaRouteResult> {
  // check if can be completes in a single valhalla call
  if (locations.length <= MAX_VALHALLA_LOCATIONS)
    return await fetchValhallaRoutePlan(locations, costing);

  // figure out how many valhalla calls need to be made
  let start = 0;
  let end = 0;
  const arraysOfLocations: Valhalla_Location[][] = [];
  do {
    end = start + MAX_VALHALLA_LOCATIONS;
    arraysOfLocations.push(locations.slice(start, end));
    start = end - 1;
  } while (end < locations.length);

  // make valhalla calls simultaneously
  const promises = arraysOfLocations.map((locationsArray) =>
    fetchValhallaRoutePlan(locationsArray, costing),
  );

  const resolvedPromises = await Promise.all(promises);

  // check all the promises resolved correctly
  if (!resolvedPromises.every(isSuccessResult)) {
    const failed = resolvedPromises.find((r) => !r.success);
    return failed!;
  }

  return {
    success: true,
    route: mergeValhallaRoutes(resolvedPromises.map((r) => r.route)),
  };
}

function isSuccessResult(
  result: ValhallaRouteResult,
): result is Extract<ValhallaRouteResult, { success: true }> {
  return result.success;
}

function mergeValhallaRoutes(routes: Valhalla_RouteRes[]): Valhalla_RouteRes {
  if (!routes.length) {
    throw new Error("Cannot merge empty route list");
  }

  const first = routes[0];
  if (!first) {
    throw new Error("Missing first route");
  }

  const additionalRoutes = routes.slice(1);

  if (!additionalRoutes.length) return first;

  additionalRoutes.forEach((route) => {
    first.trip.legs.push(...route.trip.legs);

    first.trip.summary.length += route.trip.summary.length;
    first.trip.summary.time += route.trip.summary.time;
    first.trip.summary.cost += route.trip.summary.cost;

    first.trip.summary.has_ferry ||= route.trip.summary.has_ferry;
    first.trip.summary.has_toll ||= route.trip.summary.has_toll;
    first.trip.summary.has_highway ||= route.trip.summary.has_highway;
  });

  return first;
}
